import { keyDomainFor, type SearchCredentials, type SearchEngine, type SearchKeyDomain } from '@shared/core/search';

import { algoliaApplicationId, createAlgoliaTransport } from './algolia';
import {
  type MultiSearchLeg,
  type MultiSearchResult,
  type MultiSearchTransport,
  SearchAuthError,
  type SearchTransport,
} from './types';
import { createTypesenseTransport, parseOrigins } from './typesense';

export {
  type FacetCount,
  type MultiSearchHit,
  type MultiSearchLeg,
  type MultiSearchResult,
  SearchAuthError,
  type SearchDocument,
  type SearchHighlight,
  type SearchTransport,
} from './types';

/** True when the credentials' endpoint names a host the engine's transport can call. */
export const hasUsableEndpoint = (credentials: SearchCredentials): boolean =>
  credentials.engine === 'typesense'
    ? parseOrigins(credentials.endpoint).length > 0
    : algoliaApplicationId(credentials.endpoint) !== null;

export const domainKey = (credentials: SearchCredentials, domain: SearchKeyDomain): string | undefined =>
  domain === 'orders' ? credentials.keys.orders : (credentials.keys.general ?? undefined);

const transportFor = (credentials: SearchCredentials, key: string): SearchTransport & MultiSearchTransport =>
  credentials.engine === 'typesense'
    ? createTypesenseTransport(credentials.endpoint, key)
    : createAlgoliaTransport(credentials.endpoint, key);

export function createSearchTransport(credentials: SearchCredentials, collection: string): SearchTransport | null {
  const key = domainKey(credentials, keyDomainFor(collection));
  return key === undefined ? null : transportFor(credentials, key);
}

/** Searches several collections in one round trip per key domain. */
export interface SearchClient {
  readonly engine: SearchEngine;
  /**
   * One result per leg, in leg order. A leg whose domain has no key answers empty; a leg whose
   * domain's request failed carries `error` while the other domain's legs still answer.
   * Rejects with SearchAuthError when the engine refuses a key, and rejects when `signal` aborts.
   */
  multiSearch(legs: readonly MultiSearchLeg[], signal?: AbortSignal): Promise<readonly MultiSearchResult[]>;
}

const NO_KEY_RESULT: MultiSearchResult = { found: 0, hits: [], facetCounts: {} };

const failedLeg = (reason: unknown): MultiSearchResult => ({
  ...NO_KEY_RESULT,
  error: reason instanceof Error ? reason.message : String(reason),
});

const KEY_DOMAINS: readonly SearchKeyDomain[] = ['general', 'orders'];

export function createSearchClient(credentials: SearchCredentials): SearchClient {
  return {
    engine: credentials.engine,
    async multiSearch(legs, signal) {
      const results: MultiSearchResult[] = legs.map(() => NO_KEY_RESULT);
      const outcomes = await Promise.allSettled(
        KEY_DOMAINS.map(async (domain) => {
          const key = domainKey(credentials, domain);
          const routed = legs.flatMap((leg, index) =>
            keyDomainFor(leg.collection) === domain ? [{ leg, index }] : []
          );
          if (key === undefined || routed.length === 0) return;
          try {
            const answered = await transportFor(credentials, key).multiSearch(
              routed.map(({ leg }) => leg),
              signal
            );
            routed.forEach(({ index }, offset) => {
              results[index] = answered[offset] ?? NO_KEY_RESULT;
            });
          } catch (error) {
            if (error instanceof SearchAuthError || signal?.aborted) throw error;
            routed.forEach(({ index }) => {
              results[index] = failedLeg(error);
            });
          }
        })
      );
      for (const outcome of outcomes) {
        if (outcome.status === 'rejected') throw outcome.reason;
      }
      return results;
    },
  };
}
